import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RequestService } from '@app/core/services/request/request.service';
import { FormErrorHandler } from '@core/error-handler/form-error-handler';
import { SELECTORS } from '@global/endpoints';
import { Store } from '@ngrx/store';
import { fullPostalCodeValidator, objectValidator } from '@shared/validations/custom-validations';
import { AppState } from '@store/app.state';
import * as dataLayerActions from '@store/data-layer/data-layer.actions';
import * as metadataActions from '@store/metadata/metadata.actions';
import * as optionsActions from '@store/options/options.actions';
import * as actions from '@store/pyme/pyme.actions';
import * as fromPyme from '@store/pyme/pyme.reducer';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-pyme-info',
  templateUrl: './pyme-info.page.html',
  styleUrls: ['./pyme-info.page.scss'],
})
export class PymeInfoPage implements OnInit, OnDestroy {
  classificationValues$!: Observable<any>;
  riskValues: any[] = [];
  form: FormGroup;
  errors: Record<string, any> = {};

  inputs = [
    { placeholder: '0', maxlength: 1 },
    { placeholder: '6', maxlength: 1 },
    { placeholder: '6', maxlength: 1 },
    { placeholder: '0', maxlength: 1 },
    { placeholder: '0', maxlength: 1 },
  ];

  compareOption(first: any, second: any): boolean {
    if (first === second) {
      return true;
    }

    const firstValue = first?.id ?? first;
    const secondValue = second?.id ?? second;
    return firstValue === secondValue;
  }

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly formErrorHandler: FormErrorHandler,
    private readonly store: Store<AppState>,
    private readonly requestService: RequestService,
    private readonly httpClient: HttpClient,
  ) {
    this.form = this.formBuilder.group({
      location: [null, [fullPostalCodeValidator]],
      classification: [null, [Validators.required, objectValidator]],
      risk: [null, [Validators.required, objectValidator]],
    });
  }

  // trackBy for ngFor to keep input instances stable
  trackByIndex(index: number): number {
    return index;
  }

  ngOnInit(): void {
    this.store.dispatch(new optionsActions.UpdateOptions({ isHideArrow: false, confirm: false, step: 1 }));
    this.formErrorHandler.handleErrors(this.form, this.errors);

    this.getComboBoxValues();
    this.getStoreValues();
    this.listenToClassificationChanges();
    this.saveSingleFormControl();
    this.updateDataLayer();

    this.handleAnalytics();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getStoreValues(): void {
    this.store.select(fromPyme.getClassification)
      .pipe(takeUntil(this.destroy$))
      .subscribe((classification: any) => {
        this.form.get('classification')?.setValue(classification);
        this.riskValues = classification?.subgroups || [];
      });

    this.store.select(fromPyme.getRisk)
      .pipe(takeUntil(this.destroy$))
      .subscribe((risk) => {
        this.form.get('risk')?.setValue(risk);
      });

    this.store.select(fromPyme.getLocation)
      .pipe(takeUntil(this.destroy$))
      .subscribe((location) => {
        this.form.get('location')?.setValue(location);
      });
  }

  private listenToClassificationChanges(): void {
    this.form.get('classification')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((classificationValue) => {
        this.riskValues = classificationValue?.subgroups || [];

        if (this.form.get('risk')?.value) {
          this.form.get('risk')?.setValue(null);
        }
      });
  }

  private getComboBoxValues(): void {
    this.classificationValues$ = this.requestService.get(SELECTORS);
  }

  private saveSingleFormControl(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.statusChanges.pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.form.get(key)?.valid) {
          this.store.dispatch(new actions.UpdatePyme({ [key]: this.form.get(key)?.value }));
        }
      });
    });
  }

  private updateDataLayer(): void {
    this.store.dispatch(new dataLayerActions.UpdateDataLayer({ step_number: '1', step_description: 'pyme-info' }));
  }

  navigateTo(): void {
    this.store.dispatch(new dataLayerActions.StepView('datos-comercio'));
    this.store.dispatch(new metadataActions.SendEntitysActivity(['pyme']));
    this.router.navigate(['/tarificacion']);
  }

  isZipCodeCompleted(): boolean {
    const zipCode = this.form.get('location')?.value?.zipCode;
    return zipCode?.length === 5;
  }

  isFormValid(): boolean {
    const hasState = !!this.form.get('location')?.value?.state;
    return this.form.valid && hasState;
  }

  private handleAnalytics(): void {
    sessionStorage.setItem('client', '');

    setTimeout(() => {
      try {
        const dataClient = sessionStorage.getItem('dataClient');
        const clientDataStatus = sessionStorage.getItem('client-data');

        if (window['stepView'] && (dataClient === '' || (clientDataStatus === 'completed' && dataClient === 'true'))) {
          window['stepView']('datos-comercio');
        }
      } catch (error) {
        console.error('Error executing stepView tracking', error);
      }
    }, 1000);

    const digitalData = window['digitalData'] || {};
    digitalData.step_basic = 'p1:quoting start';
    digitalData.flow_id = '1.0';
    digitalData.step_number = 'step1';
    digitalData.step_description = 'datos-comercio';
    digitalData.step_common = 'step1';
    digitalData.url = window.location.href;
    digitalData.progress_id = '1';
    window['digitalData'] = digitalData;
  }
}